import moment from 'moment';
import React, {useMemo, useState} from 'react';

import {assertAndContinue} from 'Asserts';
import {SectionConfiguration} from 'notebook/ELNConstants';
import {getDefaultDateForInsertEntryDay} from 'notebook/entries/BaseEntryUtil';
import TemplateUtil from 'notebook/entry-templates/TemplateUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import Label from 'ui-library/bs/Label';
import {ButtonStyle} from 'ui-library/buttons/Button';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import {DateInput} from 'ui-library/inputs/DatetimeInput';
import TextInput from 'ui-library/inputs/TextInput';
import Modal from 'ui-library/modals/Modal';
import {USER_EDIT_DATE_FORMAT, isValidDate} from 'util/DateUtil';

export interface InsertEntryDayModalProps {
  isUpdate: boolean;
  onRequestClose: () => void;
  onRequestSubmit: (title: string, date: string | null) => void;
  title: string;
  date: string;
  forTemplate: boolean;
  getDateBoundaries: (index: number) => {previousDate: string; nextDate: string};
  sectionConfiguration: SectionConfiguration;
  index: number;
}

function isValidTemplateDate(date: string): boolean {
  const templateDay = TemplateUtil.isoDateToTemplateDay(date);
  return 0 < templateDay && templateDay <= 1000;
}

// TODO(BNCH-92968) Rename to InsertSectionModal
export function InsertEntryDayModal({
  isUpdate,
  onRequestClose,
  onRequestSubmit,
  title: passedTitle,
  date: passedDate,
  forTemplate,
  getDateBoundaries,
  sectionConfiguration,
  index,
}: InsertEntryDayModalProps): JSX.Element | null {
  const [submitted, setSubmitted] = useState(false);
  const hasCheckboxes = sectionConfiguration.allows.title && sectionConfiguration.allows.date;
  const {previousDate, nextDate} = useMemo(() => getDateBoundaries(index), [getDateBoundaries, index]);
  const [title, setTitle] = useState(sectionConfiguration.allows.title ? passedTitle : '');
  const [disableTitle, setDisableTitle] = useState(Boolean(!passedTitle) && hasCheckboxes);
  const [disableDate, setDisableDate] = useState(isUpdate ? Boolean(!passedDate) && hasCheckboxes : false);
  const defaultDate = useMemo(
    () => getDefaultDateForInsertEntryDay(passedDate, previousDate, nextDate, forTemplate),
    [passedDate, previousDate, nextDate, forTemplate]
  );
  const [date, setDate] = useState(sectionConfiguration.allows.date ? defaultDate : '');
  assertAndContinue(
    sectionConfiguration.allows.title || sectionConfiguration.allows.date,
    'InsertEntryDayModal expects at least one of title or date to be enabled.'
  );

  return (
    <Modal
      title={isUpdate ? 'Update section' : 'Insert section'}
      onRequestClose={onRequestClose}
      onRequestSubmit={() => {
        // handle double clicks
        if (submitted) {
          return;
        }
        setSubmitted(true);
        onRequestSubmit(disableTitle ? '' : title, disableDate ? '' : date);
      }}
      buttons={[
        {
          text: isUpdate ? 'Update' : 'Insert',
          style: ButtonStyle.SECONDARY,
          submit: true,
          // Disable the submit button if:
          //  1. Both title and date are missing/empty
          //  2. Both title and date are disabled
          //  3. Title is disabled and missing/empty date (aka missing date and title)
          //  4. Date is disabled and missing/empty title (aka missing date and title)
          //  5. Date is before previousDate
          //  6. Date is after nextDate
          //  7. Is not a template and date is invalid
          //  8. Is a template and date is out of bounds of 1 - 1000
          //  9. If title is allowed and enabled and no title is provided
          disabled:
            submitted ||
            (!date && !title) ||
            (disableTitle && disableDate) ||
            (disableTitle && !date) ||
            (disableDate && !title) ||
            (previousDate && date && !disableDate && moment(date).isBefore(previousDate, 'day')) ||
            (nextDate && date && !disableDate && moment(date).isAfter(nextDate, 'day')) ||
            (!forTemplate && !isValidDate({textValue: date, validDateFormats: [USER_EDIT_DATE_FORMAT]})) ||
            (forTemplate && !isValidTemplateDate(date)),
        },
      ]}
    >
      {sectionConfiguration.allows.title && (
        <>
          <Label>Section Name</Label>
          <TextInput
            onChange={(e) => {
              setTitle(e.target.value);
            }}
            disabled={disableTitle}
            defaultValue={title}
            {...getTestProps({component: 'InsertEntryDayModal', element: 'titleNameInputFields'})}
          />
        </>
      )}
      {hasCheckboxes && (
        <CheckboxInput
          className="sectionModal-input"
          checked={disableTitle}
          onChange={(e) => {
            setDisableTitle(e.target.checked);
          }}
          id="dontUseName"
          label="Don't use section name"
          dataTestAttrs={{component: 'InsertEntryDayModal', element: 'titleEnabledInputFields'}}
        />
      )}
      <br />
      {forTemplate ? (
        <>
          <Label>Day</Label>
          <input
            min={1}
            max={1000}
            type="number"
            disabled={disableDate}
            value={TemplateUtil.isoDateToTemplateDay(date)}
            onChange={(e) => {
              setDate(TemplateUtil.templateDayToIsoDate(Number(e.target.value)));
            }}
          />
        </>
      ) : (
        sectionConfiguration.allows.date && (
          <>
            <Label>Section Date</Label>
            <DateInput
              value={date}
              onChange={(e) => {
                setDate(e);
              }}
              isDisabled={disableDate}
              datetimePresentationalFormat="YYYY-MM-DD"
              validDatetimeInputFormats={['YYYY-MM-DD']}
              placeholder={date}
              min={previousDate}
              max={nextDate}
            />
          </>
        )
      )}
      {hasCheckboxes && (
        <CheckboxInput
          className="sectionModal-input"
          checked={disableDate}
          onChange={(e) => {
            setDisableDate(e.target.checked);
          }}
          id="dontUseDate"
          label="Don't use section date"
          dataTestAttrs={{component: 'InsertEntryDayModal', element: 'dateEnabledInputFields'}}
        />
      )}
    </Modal>
  );
}
